import { Observable } from 'rxjs'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import { buffer, filter, ignoreElements, map, tap, throttleTime } from 'rxjs/operators'
import { GlobalState } from 'store'
import { Application, CommittingServicingEvent, DealDetail, DealEvent, DealStatus, NegotiationDetail, NegotiationStatus } from 'api'
import { createNegotiationDetailSelector } from 'selectors/negotiations'
import { AudioService, NotificationService } from 'services'
import { ActionsUnion, createAction, hasBrowserNotificationsEnabled, isOpenFin } from 'utils'
import { getLiveStatusTextPriced, getLiveSubStatusText, isImportantStatus, isWithdrawnOrSubject } from 'view/routes/requests/shared/utils'

import { TradingEntityHelper, TradingEntityWithAliasHelper } from 'model'
import { isNil } from 'lodash-es'
import { getDealStatusTextDone, getDealSubstatusText, getServicingStatusText } from 'view/routes/deals/components/utils'
import { createUserPreferenceSelector } from '../selectors/userPreferenceSelectors'
import {
  ToastNotificationMessage,
  getLiveStatusToastColor,
  getToastPriorityFromToastColor,
  getServicingStatusToastColor,
  ToastColor
} from 'view/routes/requests/shared/toastUtils'
import { BTQAction } from 'epics'
export interface ToastDependencies {
  notificationService: NotificationService
  audioService: AudioService
}

export enum NotificationsToastActionTypes {
  RFQ_TOAST_CREATE = 'RFQ_TOAST_CREATE',
  DEAL_TOAST_CREATE = 'DEAL_TOAST_CREATE'
}

export const NotificationsToastActions = {
  createRfqToast: (negotiationId: string, ownOrder: boolean) =>
    createAction(NotificationsToastActionTypes.RFQ_TOAST_CREATE, { negotiationId, ownOrder }),
  createDealToast: (dealDetail: DealDetail, servicingEvent?: CommittingServicingEvent, replacedWithDealId?: string | null) =>
    createAction(NotificationsToastActionTypes.DEAL_TOAST_CREATE, { dealDetail, servicingEvent, replacedWithDealId })
}

type CreateRfqToastAction = ReturnType<typeof NotificationsToastActions.createRfqToast>
type CreateDealToastAction = ReturnType<typeof NotificationsToastActions.createDealToast>

export type NotificationsToastActions = ActionsUnion<typeof NotificationsToastActions>

export function rfqToastEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>, dependencies: ToastDependencies) {
  const toastNegotiationDetail$ = action$.pipe(
    ofType<BTQAction, NotificationsToastActionTypes.RFQ_TOAST_CREATE, NotificationsToastActions>(
      NotificationsToastActionTypes.RFQ_TOAST_CREATE
    ),
    filter(() => {
      return isTradingUser(state$.value)
    }),
    map((toastAction: CreateRfqToastAction) => {
      const negotiationIdSelector = () => toastAction.payload.negotiationId
      const ownOrderSelector = () => toastAction.payload.ownOrder
      const historySelector = () => false

      return createNegotiationDetailSelector(negotiationIdSelector, ownOrderSelector, historySelector)(state$.value, {})
    }),
    filter((negotiationDetail: NegotiationDetail | null): negotiationDetail is NegotiationDetail => {
      if (!negotiationDetail) {
        return false
      }

      const userPreferencesSelector = createUserPreferenceSelector()
      const userPreferences = userPreferencesSelector(state$.value)

      if (isOpenFin()) {
        return userPreferences.notificationsEnabled
      } else {
        return hasBrowserNotificationsEnabled()
      }
    })
  )

  return toastNegotiationDetail$.pipe(
    buffer(toastNegotiationDetail$.pipe(throttleTime(5000, undefined, { trailing: true, leading: false }))),
    map(negotiationDetails => {
      return negotiationDetails.reduce<Record<string, ToastNotificationMessage>>((acc, negotiationDetail) => {
        const statusText = getLiveStatusTextPriced(negotiationDetail)

        // All the properties that aggregate a rfq notification
        const aggregateKey = `${negotiationDetail.negotiation.status}/${statusText}/${negotiationDetail.orderFund?.id}/${negotiationDetail.counterparty?.id}`

        if (acc[aggregateKey]) {
          acc[aggregateKey] = {
            ...acc[aggregateKey],
            count: acc[aggregateKey].count + 1
          }
          return acc
        }

        const organisationName = negotiationDetail.ownOrder
          ? isNil(negotiationDetail.counterpartyFirm)
            ? ''
            : `${negotiationDetail.counterpartyFirm.legalName}`
          : isNil(negotiationDetail.orderFundFirm)
          ? ''
          : `${negotiationDetail.orderFundFirm.legalName}`

        const initiatorTradingEntityName = negotiationDetail.orderFund
          ? negotiationDetail.ownOrder
            ? `${negotiationDetail.orderFund.publishedName}`
            : negotiationDetail?.negotiation?.counterpartyAlias ?? `${TradingEntityHelper.getName(negotiationDetail.orderFund)}`
          : ''

        const subStatusText = getLiveSubStatusText(negotiationDetail)
        const statusColour = getLiveStatusToastColor(negotiationDetail)
        const priority = getToastPriorityFromToastColor(statusColour)

        acc[aggregateKey] = {
          tradingEntityName: initiatorTradingEntityName,
          organisationName,
          statusText,
          subStatusText,
          statusColour,
          priority,
          subStatusTextGrey:
            isWithdrawnOrSubject(negotiationDetail.negotiation.status) ||
            negotiationDetail.negotiation.status === NegotiationStatus.CANCELLED,
          count: 1,
          isDead: negotiationDetail.negotiation.status === NegotiationStatus.CANCELLED,
          isImportant: isImportantStatus(negotiationDetail.negotiation.status),
          negotiationProps: {
            status: negotiationDetail.negotiation.status
          },
          onClickPath: getNegotiationNotificationOnClickPath(negotiationDetail)
        }

        return acc
      }, {})
    }),
    tap((toastMessages: Record<string, ToastNotificationMessage>) => {
      const userPreferencesSelector = createUserPreferenceSelector()
      const userPreferences = userPreferencesSelector(state$.value)

      if (userPreferences.playAudibleAlertOnNotification && isOpenFin()) {
        dependencies.audioService.playNotificationAlert()
      }

      Object.values(toastMessages).forEach(toastMessage => {
        dependencies.notificationService.createNotification(toastMessage)
      })
    }),
    ignoreElements()
  )
}

export function dealToastEpic(action$: Observable<BTQAction>, state$: StateObservable<GlobalState>, dependencies: ToastDependencies) {
  const dealToastAction$ = action$.pipe(
    ofType<BTQAction, NotificationsToastActionTypes.DEAL_TOAST_CREATE, NotificationsToastActions>(
      NotificationsToastActionTypes.DEAL_TOAST_CREATE
    ),
    filter(() => isTradingUser(state$.value)),
    filter((_dealToastAction: CreateDealToastAction) => {
      const userPreferencesSelector = createUserPreferenceSelector()
      const userPreferences = userPreferencesSelector(state$.value)

      if (isOpenFin()) {
        return userPreferences.notificationsEnabled
      } else {
        return hasBrowserNotificationsEnabled()
      }
    })
  )

  return dealToastAction$.pipe(
    buffer(dealToastAction$.pipe(throttleTime(5000, undefined, { trailing: true, leading: false }))),
    map((dealToastActions: CreateDealToastAction[]) => {
      return dealToastActions.reduce<Record<string, ToastNotificationMessage>>((acc, dealToastAction) => {
        const {
          payload: { dealDetail, servicingEvent }
        } = dealToastAction

        const tradingEntityKey = `${dealDetail.orderFund?.id}/${dealDetail.counterparty?.id}`

        if (!tradingEntityKey) {
          return acc
        }

        const tradingEntityName = dealDetail.orderFund
          ? dealDetail.ownOrder
            ? `${dealDetail.orderFund.publishedName}`
            : `${TradingEntityWithAliasHelper.getName(dealDetail.orderFund)}`
          : ''

        const organisationName = dealDetail.ownOrder
          ? isNil(dealDetail.counterpartyFirm)
            ? ''
            : `${dealDetail.counterpartyFirm.legalName}`
          : isNil(dealDetail.orderFundFirm)
          ? ''
          : `${dealDetail.orderFundFirm.legalName}`

        const statusText = servicingEvent
          ? getServicingStatusText(servicingEvent, dealDetail.deal.servicingDetails?.servicingType.servicingType || undefined)
          : getDealStatusTextDone(dealDetail, false)

        const subStatusText = getDealSubstatusText(dealDetail.deal)
        const statusColour = servicingEvent ? getServicingStatusToastColor(servicingEvent) : ToastColor.GRAY
        const priority = getToastPriorityFromToastColor(statusColour)
        // All the properties that aggregate a deal notification
        const aggregateKey = `${dealDetail.deal.status}/${tradingEntityKey}/${statusText}/${subStatusText}/${servicingEvent}`

        acc[aggregateKey] = {
          tradingEntityName,
          organisationName,
          statusText,
          subStatusText,
          statusColour,
          priority,
          subStatusTextGrey: dealDetail.deal.amended,
          count: acc[aggregateKey] ? acc[aggregateKey].count + 1 : 1,
          isDead: dealDetail.deal.status === DealStatus.CANCELLED,
          isImportant: true,
          dealProps: {
            status: dealDetail.deal.status,
            servicingEvent
          },
          onClickPath: getDealNotificationOnClickPath(servicingEvent)
        }

        return acc
      }, {})
    }),
    tap((toastMessages: Record<string, ToastNotificationMessage>) => {
      const userPreferencesSelector = createUserPreferenceSelector()
      const userPreferences = userPreferencesSelector(state$.value)

      if (userPreferences.playAudibleAlertOnNotification && isOpenFin()) {
        dependencies.audioService.playNotificationAlert()
      }

      Object.values(toastMessages).forEach(toastMessage => dependencies.notificationService.createNotification(toastMessage))
    }),
    ignoreElements()
  )
}

const isTradingUser = (state: GlobalState) =>
  !!(state.connectionStore.authorisedUser && state.connectionStore.authorisedUser.application === Application.TRADING_GATEWAY)

const getDealNotificationOnClickPath = (servicingEvent?: CommittingServicingEvent) => {
  if (servicingEvent) {
    switch (servicingEvent) {
      case DealEvent.SERVICING_SEND:
      case DealEvent.SERVICING_START_RERATE:
      case DealEvent.SERVICING_START_SUBSTITUTE:
      case DealEvent.SERVICING_START_TERMINATE:
      case DealEvent.SERVICING_START_RETURN_RECALL:
      case DealEvent.SERVICING_START_ROLL_IN_PLACE:
        return 'midTradeEvents'
      default:
        return 'trades'
    }
  } else {
    return 'trades'
  }
}

const getNegotiationNotificationOnClickPath = (negotiationDetail: NegotiationDetail) => {
  const status = negotiationDetail.negotiation.status

  if (status === NegotiationStatus.ACCEPTED) {
    return 'trades'
  }

  return negotiationDetail.ownOrder ? 'initiated' : 'incoming'
}

export const toastEpic = combineEpics(rfqToastEpic, dealToastEpic)
